// The library's Delete of one file (S1 decision 3): it asks first, then moves the file to the
// trash and reads the brand's library lists again; a refusal reads them and the access scope
// again, as the hint that offered the button may be stale. Shared by the library page and the
// Studio's Library tab (G1 decision 29), each from its card or its preview.
import { Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useDeleteAsset } from "../../api/generated/endpoints/assets/assets";
import type { Asset } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";
import { refreshAccessScope } from "../../api/session";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { ErrorAlert } from "../ErrorAlert";
import { useTrashStay } from "./trash-days";

export function useAssetDelete(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<Asset | undefined>(undefined);
  const stay = useTrashStay(teamId, brandId, deleting !== undefined);
  const refresh = () => refreshLibraryLists(queryClient, teamId, brandId);
  const remove = useDeleteAsset({
    mutation: {
      onSuccess: async () => {
        setDeleting(undefined);
        await refresh();
      },
      onError: () => Promise.all([refresh(), refreshAccessScope(queryClient)]),
    },
  });
  const dialog = (
    <ConfirmModal
      opened={deleting !== undefined}
      title={`Delete ${deleting?.name ?? "this file"}?`}
      confirmLabel="Move to trash"
      onConfirm={() => {
        if (deleting !== undefined) remove.mutate({ teamId, brandId, assetId: deleting.id });
      }}
      onCancel={() => {
        remove.reset();
        setDeleting(undefined);
      }}
      pending={remove.isPending}
      error={remove.isError ? <ErrorAlert error={remove.error} /> : null}
    >
      <Text size="sm">It stays {stay}, where anyone who may delete it can restore it.</Text>
    </ConfirmModal>
  );
  return {
    /** Asks about this file. */
    ask: (asset: Asset) => setDeleting(asset),
    /** The question is open (a preview under it waits). */
    asking: deleting !== undefined,
    dialog,
  };
}
